import { Checkbox, Label, Skeleton, cn } from '@design-system';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ReplyPreview } from '../api/api';
import { EmailFrame } from './EmailFrame';

function YesNo({
  value,
  onChange,
  label,
}: {
  value: boolean;
  onChange: (value: boolean) => void;
  label: string;
}) {
  return (
    <div
      className="inline-flex gap-0.5 rounded-[9px] bg-zinc-200/70 p-[3px]"
      role="group"
      aria-label={label}
    >
      {[true, false].map(option => (
        <button
          key={String(option)}
          type="button"
          aria-pressed={value === option}
          onClick={() => onChange(option)}
          className={cn(
            'h-[38px] rounded-[7px] px-3.5 text-[13px] font-semibold',
            value === option ? 'bg-white text-zinc-900 shadow-sm' : 'bg-transparent text-zinc-600',
          )}
        >
          {option ? 'Yes' : 'No'}
        </button>
      ))}
    </div>
  );
}

/**
 * The reply as the advisor's inbox would show it: the real email the server builds, filled in for
 * a sample client, with a Yes/No switch for each part that changes.
 */
export function EmailPreview(props: {
  query: UseQueryResult<ReplyPreview>;
  setFlag: (name: string, value: boolean) => void;
  highlight: boolean;
  setHighlight: (value: boolean) => void;
}) {
  const preview = props.query.data;

  return (
    <section className="flex flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white">
      <div className="flex flex-col gap-2.5 border-b border-zinc-200 bg-zinc-50 px-[18px] py-3.5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-base font-semibold">Preview</h2>
          <span className="text-[13px] text-zinc-600">
            Filled in for a sample client, Sam Sample
          </span>
        </div>
        {preview?.conditions.map(condition => (
          <div
            key={condition.name}
            className="flex flex-wrap items-center gap-x-3 gap-y-2"
          >
            <span className="text-[13px] text-zinc-700">Show it as if {condition.label}:</span>
            <YesNo
              value={condition.value}
              onChange={value => props.setFlag(condition.name, value)}
              label={`Show it as if ${condition.label}`}
            />
          </div>
        ))}
        <div className="flex min-h-11 items-center gap-2">
          <Checkbox
            id="highlight-added"
            checked={props.highlight}
            onCheckedChange={value => props.setHighlight(value === true)}
          />
          <Label
            htmlFor="highlight-added"
            className="text-[13px] font-normal text-zinc-700"
          >
            Highlight added information
          </Label>
        </div>
      </div>

      <div className="px-6 pb-7 pt-5">
        {!preview ? (
          <Skeleton className="h-72 w-full" />
        ) : !preview.html ? (
          <p className="text-sm text-zinc-600">
            The preview will show once the email can be read. {preview.problems[0]?.message}
          </p>
        ) : (
          <EmailFrame
            title="Email preview"
            subject={preview.subject ?? ''}
            from={preview.from ?? ''}
            html={preview.html}
            dimmed={props.query.isFetching}
          />
        )}
      </div>
    </section>
  );
}
